import { Link } from "@/lib/router-compat";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ArrowRight, Package } from "lucide-react";
import { formatPrice, productPath, type Product, type ProductCategory } from "@/lib/products";

interface Props {
  product: Product;
  category: ProductCategory;
}

const ProductCard = ({ product, category }: Props) => {
  const price = formatPrice(product);
  return (
    <Link
      to={productPath(category.service_slug, product.slug)}
      className="group block h-full w-full"
      aria-label={product.name}
    >
      <Card className="h-full p-5 rounded-2xl border-border/60 hover:shadow-lg transition-shadow flex flex-col">
        <div className="aspect-[4/3] rounded-xl bg-muted/40 mb-4 overflow-hidden flex items-center justify-center">
          {product.image_url ? (
            <img
              src={product.image_url}
              alt={product.name}
              loading="lazy"
              className="w-full h-full object-cover"
            />
          ) : (
            <Package className="text-muted-foreground" size={40} aria-hidden />
          )}
        </div>
        <div className="flex items-center gap-2 mb-1">
          {product.brand && (
            <span className="text-xs uppercase tracking-wide text-muted-foreground">
              {product.brand}
            </span>
          )}
          {product.badge && <Badge variant="secondary">{product.badge}</Badge>}
        </div>
        <h3 className="heading-3 group-hover:text-primary transition-colors">
          {product.name}
        </h3>
        {product.short_description && (
          <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
            {product.short_description}
          </p>
        )}
        <div className="mt-auto pt-4 flex items-center justify-between">
          <span className="text-sm font-medium text-foreground">{price ?? "Begär offert"}</span>
          <ArrowRight
            className="text-primary group-hover:translate-x-1 transition-transform"
            size={18}
          />
        </div>
      </Card>
    </Link>
  );
};

export default ProductCard;
